import { z } from "zod";

import { VibeGitProjectIdPathVarsSchema } from "./git-projects.schemas";

/**
 * A push credential bound to ONE `VibeGitProject`, and nothing else — the only
 * git push credential the API serves.
 *
 * The tenant also holds an org-wide token: one secret that pushes — and
 * force-pushes — to EVERY repository the org owns. No server read uses it and
 * no route returns it
 * (`apps/backend/src/__governance__/org-git-credential-is-never-served.spec.ts`).
 *
 * `username` here is a per-project MACHINE USER, not the platform admin. It is
 * a `write` collaborator on this project's repository alone, which was measured
 * to deny it read on any sibling repo, deny org enumeration, and deny deleting
 * even its own repo. Forgejo's native repo-scoped token would be weaker, and is
 * not creatable through the API on the version we pin.
 *
 * `cloneUrl` is the FULL, token-free remote for this one repository, never a
 * base the caller appends a repo name to: a per-project credential has exactly
 * one repo it can reach, so handing back a base to be completed would invite
 * pointing it at a repo the token cannot use.
 */
export const VibeGitProjectCredentialsSchema = z.object({
  /// The project this credential is bound to. Echoed back so a caller holding
  /// several credentials can never mismatch one to the wrong remote.
  gitProjectId: z.string(),
  /// Project name, which is also its Forgejo repository name.
  gitProjectName: z.string(),
  /// The tenant git host's public DNS name, e.g. `git.org-abc.gpt.nexus`.
  gitHostName: z.string(),
  /// The Forgejo org every tenant repo lives under.
  forgejoOrg: z.string(),
  /// The per-project machine user: `vibe-p-<gitProjectId without dashes>`.
  /// This is the basic-auth username for git-over-HTTP with `pushToken`.
  username: z.string(),
  /// The decrypted per-project Forgejo token. SECRET — never log it.
  pushToken: z.string(),
  /// Token-free remote for THIS repository: `https://<host>/<org>/<name>.git`.
  cloneUrl: z.string()
});

export type VibeGitProjectCredentialsDto = z.infer<typeof VibeGitProjectCredentialsSchema>;

/** Same `:id` addressing as every other standalone git-project route. */
export const GetVibeGitProjectCredentialsPathVarsSchema = VibeGitProjectIdPathVarsSchema;

export const GetVibeGitProjectCredentialsResponseSchema = z.object({
  credentials: VibeGitProjectCredentialsSchema
});
